import cn from "classnames";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import openPopupOperation from "editor/src/store/app/module/popup/operation/openPopupOperation";
import { Unit } from "editor/src/store/editor/types";
import { useDispatch, useSelector } from "editor/src/store/hooks";
import getProductVariationSpecificTitle from "editor/src/store/variants/helpers/getProductVariationSpecificTitle";
import {
  DesignOption,
  MultiOptions,
  Product,
  VariationGroup,
} from "editor/src/store/variants/types";

import useSpreadPreview from "editor/src/util/design/useSpreadPreview";

import IconBin from "editor/src/component/Icon/IconBin";
import { PopupName } from "editor/src/component/Popup/popups";
import getNotGroupedValues from "editor/src/component/ProductVariantList/getNotGroupedValues";
import getUnavailableValues from "editor/src/component/ProductVariantList/getUnavailableValues";
import VariantLock from "editor/src/component/ProductVariantList/VariantLock";
import { RequestRenderFn } from "editor/src/component/SpreadPreview";

import clampSpreadIndex from "./clampSpreadIndex";
import styles from "./index.module.scss";

export interface Props {
  group: VariationGroup;
  options: MultiOptions;
  spreadIndex: number;
  product: Product;
  unit: Unit;
  isSelected: boolean;
  linkEnabled: boolean;
  unavailableProducts: Product["unavailableProducts"];
  onSelect: (groupKey: string, designOption?: DesignOption) => void;
  onLinkToggle: (group: VariationGroup) => void;
  onDelete?: (key: string) => void;
  requestRender: RequestRenderFn;
  className?: string;
  overrideStyles?: boolean;
}

export interface Ref {
  getHeight(): number;
  setY(y: number): void;
}

function ProductVariantEntry(
  {
    group,
    isSelected,
    linkEnabled,
    onSelect,
    onLinkToggle,
    onDelete,
    requestRender,
    product,
    unit,
    spreadIndex,
    options,
    className,
    overrideStyles = true,
    unavailableProducts,
  }: Props,
  ref: React.Ref<Ref>,
) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const divRef = useRef<HTMLDivElement>(null);
  const [y, setY] = useState(-1);
  const [positionned, setPositionned] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      getHeight: () => divRef.current?.clientHeight || 0,
      setY,
    }),
    [],
  );

  const notGroupedValues = useMemo(
    () =>
      getNotGroupedValues(
        options,
        product.groupBy,
        product.productControls,
        group.variationsInfo,
      ),
    [options, product.groupBy, product.productControls, group.variationsInfo],
  );

  const unAvailableValues = useMemo(
    () =>
      getUnavailableValues(
        options,
        product.groupBy,
        product.productControls,
        group.variationsInfo,
      ),
    [options, product.groupBy, product.productControls, group.variationsInfo],
  );

  useEffect(() => {
    if (y === -1) {
      return undefined;
    }

    const handle = setTimeout(() => setPositionned(true), 0);
    return () => window.clearTimeout(handle);
  }, [y === -1]);

  const onLinkClick = useCallback(() => {
    onLinkToggle(group);
  }, [group, onLinkToggle]);

  const onEntryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(group.key);
  };

  const onEntryDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(
      openPopupOperation(PopupName.LOCAL_CONFIRMATION_POPUP, {
        popupTitle: t("Delete variant"),
        textLines: [
          t('Are you sure you want to delete "{{variant}}"?', {
            variant: variantTitle,
          }),
        ],
        options: [
          {
            title: t("editor-cancel"),
          },
          {
            title: t("Delete"),
            onConfirm: () => onDelete?.(group.key),
          },
        ],
        hideTitleIcon: true,
      }),
    );
  };

  const { designData } = group.variationsInfo[0];
  // GAPI-30848 — pick the spread to thumbnail with three layered preferences:
  //
  //   1. The active design's currently-focused spread name when this variant
  //      ALSO has a cached scene for it (most accurate — chip mirrors what
  //      the user is editing on the canvas).
  //   2. Any spread name this variant has a cached scene for (keeps the
  //      chip on the correct GARMENT COLOUR even when the active spread
  //      belongs to a secondary tech this variant's catalogue uid doesn't
  //      natively serve — e.g. user picked Back→DTG so active is
  //      `back-dtg`, but Berry/Black are catalogue-default DTF uids whose
  //      scenes are `front-dtf`/`back-dtf`; we'd rather render Berry-
  //      front-dtf than leak Athletic-Heather-back-dtg through the
  //      `state.design.spreadBackgrounds` fallback in useSpreadPreview).
  //   3. The active spread name even without a scene (renders blank but
  //      keeps spread metadata consistent with the active design).
  //   4. The index passed in (legacy fallback).
  //
  // `spreadIndex` (= `state.editor.currentSpreadIndex`) indexes the ACTIVE
  // design's spreads, which may be in a different order than this
  // variant's `designData.spreads` — especially for multi-tech variants
  // where the bridge augments spreads in unpredictable orders.
  const activeSpreadName = useSelector(
    (state) =>
      state.design.designData?.spreads?.[state.editor.currentSpreadIndex]?.name,
  );
  const variantScenes = useSelector(
    (state) => state.variants.spreadImages[group.key]?.spreadBackgrounds,
  );
  // GAPI-30848 — tech-suffix-insensitive cached-scene matching. The
  // chip needs to know whether the variant has a scene we can render
  // FOR THE ACTIVE SPREAD. Bella+Canvas 8800's variant cache stores
  // scenes under the catalogue's default-tech names (`front-dtf`,
  // `back-dtf`); when the customer is editing the `front-dtg` area we
  // want the chip to thumbnail the FRONT (matching the customer's
  // active view) using the cached `front-dtf` scene's garment image —
  // same tank, different print process. Without this fallback the
  // chip falls down to tier 2 and grabs whichever cached scene exists
  // (typically `back-dtf`), so the customer working on Front sees a
  // Back-pumpkin thumbnail in every variant card.
  const MULTI_TECH_SUFFIX_RE = /-(dtg|dtf|embroidery|emb|sublimation)$/i;
  const stripTech = (name: string) => name.replace(MULTI_TECH_SUFFIX_RE, "");
  const variantSceneAreaNames = useMemo(() => {
    const exact = new Set<string>();
    const stripped = new Set<string>();
    variantScenes?.forEach((s) => {
      if (!s?.name) return;
      exact.add(s.name);
      stripped.add(stripTech(s.name));
    });
    return { exact, stripped };
  }, [variantScenes]);
  const hasCachedSceneFor = (spreadName: string | undefined): boolean => {
    if (!spreadName) return false;
    if (variantSceneAreaNames.exact.has(spreadName)) return true;
    return variantSceneAreaNames.stripped.has(stripTech(spreadName));
  };
  const activeSpreadMatch = activeSpreadName
    ? designData?.spreads?.find((s) => s?.name === activeSpreadName)
    : undefined;
  const spread =
    (activeSpreadMatch && hasCachedSceneFor(activeSpreadMatch.name)
      ? activeSpreadMatch
      : undefined) ??
    (variantSceneAreaNames.exact.size
      ? designData?.spreads?.find((s) => hasCachedSceneFor(s?.name))
      : undefined) ??
    activeSpreadMatch ??
    designData?.spreads[clampSpreadIndex(spreadIndex, designData?.spreads?.length)];
  const matchedSpreadIndex =
    spread && designData ? designData.spreads.indexOf(spread) : spreadIndex;
  const spreadGroup = designData?.spread_groups?.find((group) =>
    group.spreadIndexes.includes(matchedSpreadIndex),
  );
  const variantTitle = getProductVariationSpecificTitle(
    product,
    group.variationsInfo[0],
    unit,
  );

  const { preview } = useSpreadPreview(
    requestRender,
    spread,
    group.key,
    matchedSpreadIndex,
    { dimension: "height", value: 40 },
    { showBlanks: true, showEmptyImages: true },
    undefined,
    spreadGroup,
  );

  const style =
    y === -1 ? { opacity: 0 } : { transform: `translate3d(0,${y}px,0)` };
  const hasUnavailableProducts = group.variationsInfo.some(
    (variation) => !!unavailableProducts[variation.variation.productUid],
  );

  return (
    <div
      className={cn(
        className,
        styles.ProductVariantEntry,
        "cy-variant",
        "intro-tour-variants-entry",
        {
          [styles.selected]: isSelected,
          [styles.positionned]: positionned,
          "cy-selected": isSelected,
        },
      )}
      onClick={onEntryClick}
      ref={divRef}
      style={overrideStyles ? style : undefined}
    >
      <div className={styles.content}>
        <div
          className={cn(styles.entryContent, {
            [styles.outOfStock]: group.outOfStock,
          })}
        >
          <div className={styles.thumbnail}>
            {preview && <img src={preview.dataURL} alt={group.title} />}
          </div>
          <div className={styles.entryText}>
            {variantTitle}
            {notGroupedValues && (
              <div>
                {notGroupedValues}
                {(unAvailableValues?.length ?? 0) > 0 && (
                  <span className={styles.unavailable}>
                    ,{unAvailableValues}
                  </span>
                )}
              </div>
            )}
          </div>
          {onDelete && (
            <IconBin
              className={cn(styles.deleteIcon, "cy-variant-delete-button")}
              onClick={onEntryDeleteClick}
            />
          )}
          {linkEnabled && !group.linkingDisabled && (
            <VariantLock isLinked={group.linked} onToggle={onLinkClick} />
          )}
        </div>
        {hasUnavailableProducts && (
          <div className={styles.entryUnavailable}>
            {t("Some or all products are unavailable")}
          </div>
        )}
        {!hasUnavailableProducts && group.outOfStock && (
          <div className={styles.entryOutOfStock}>
            {t("editor-product-out-of-stock-warning")}
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(React.forwardRef(ProductVariantEntry));
